import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUpdateProductMutation } from "../features/products/productsApiSlice";
import useAuth from "../hooks/useAuth";
import formatNumber, { handlePriceInput, handleWholeNumberInput } from "../utility/formatNumber";
import InputBox from "../components/InputBox";
import DropdownInput from "../components/DropdownInput";
import { productCondition, ROUTES } from "../assets/data";
import MultipleImageInput from "../components/MultipleImageInput";
import RichTextEditor from "../components/RichTextEditor";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { editInStore, updateStoreAfterEdit } from "../features/products/productSlice";
import toast from "react-hot-toast";
import PlaceholderImage from "../assets/images/placeholder.jpg";
import useSubCategory from "../hooks/useSubCategory";
import useBrand from "../hooks/useBrand";
import useMyProduct from "../hooks/useMyProduct";
import Loading from "../components/Loading";
import LoadingOverlay from "../components/LoadingOverlay";

const updateProductSchema = (need) => {
  const baseSchema = {
    productName: yup.string().required("Product name is required"),
    coverDetail: yup.string().required("Cover detail is required"),
    description: yup.string(),
    productImages: yup.array().min(0, ""),
  };
  const sellSchema = {
    ...baseSchema,
    price: yup.string().required("Price is required")
      .test("is-valid-price", "Please enter a valid price", (value) => {
        if (!value) return false;
        const numericValue = value.replace(/,/g, "");
        return !isNaN(numericValue) && parseFloat(numericValue) > 0;
      }),
    brand: yup.string().required("Brand is required"),
    category: yup.string().required("Category is required"),
    condition: yup.string().required("Condition is required"),
    stock: yup.string().required("Stock is required")
      .test("is-valid-stock", "Please enter a valid stock quantity", (value) => {
        if (!value) return false;
        return !isNaN(value) && parseInt(value) > 0;
      })
  };
  const requestSchema = {
    ...baseSchema,
    price: yup.string()
      .test("is-valid-price", "Please enter a valid price", (value) => {
        if (!value) return true;
        const numericValue = value.replace(/,/g, "");
        return !isNaN(numericValue) && parseFloat(numericValue) > 0;
      }),
    brand: yup.string(),
    category: yup.string(),
    condition: yup.string(),
    stock: yup.string()
      .test("is-valid-stock", "Please enter a valid stock quantity", (value) => {
        if (!value) return true; // Empty is allowed for request
        return !isNaN(value) && parseInt(value) > 0;
      })
  };

  if (need === "Sell")  return yup.object().shape(sellSchema);
  return yup.object().shape(requestSchema);
};

const UpdateProduct = () => {
  const { id: productId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useAuth();
  const [updateProduct, { isLoading, isSuccess, isError, error }] = useUpdateProductMutation();
  
  // Use custom hooks
  const { myProducts, isLoading: isLoadingProducts, error: productsError } = useMyProduct();
  const { subCategories, isLoading: isSubCategoryLoading, error: isSubCategoryError } = useSubCategory();
  const { brands, isLoading: brandsLoading, error: brandsError } = useBrand();

  // State hooks
  const [need, setNeed] = useState(null); // Initialize as null instead of defaulting to "Sell"
  const [pOldImages, setPOldImages] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);
  const [productImages, setProductImages] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [iOrder, setIOrder] = useState([]);

  const [isRedirecting, setIsRedirecting] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Find the product
  const product = myProducts?.find(p => p?._id === productId);

  // Set need type when product data is loaded
  useEffect(() => {
    if (product) {
      setNeed(product.need);
    }
  }, [product]);

  // Initialize form
  const { register, handleSubmit, formState: { errors, isSubmitted }, setValue, watch, trigger, control } = useForm({
    resolver: yupResolver(updateProductSchema(need)),
    mode: "onChange",
    defaultValues: { 
      productName: product?.product_name || "", 
      brand: product?.brand || "",
      condition: product?.condition || "", 
      category: product?.sub_category || "", 
      price: product?.price || "", 
      coverDetail: product?.cover_detail || "", 
      description: product?.description || "", 
      stock: product?.stock || "", 
      need: product?.need || "", 
      productImages: [] 
    }
  });

  // Find current brand and subcategory names
  const currentBrand = product?.brand ? brands?.find(b => b._id === product.brand)?.name : "";
  const currentSubCategory = product?.sub_category ? subCategories?.find(sc => sc._id === product.sub_category)?.name : "";

  // Set initial values when product is loaded
  useEffect(() => { 
    if(product) {
      setValue("productName", product.product_name);
      setValue("brand", product.brand);
      setValue("condition", product.condition);
      setValue("category", product.sub_category);
      setValue("price", product.price);
      setValue("description", product.description);
      setValue("coverDetail", product.cover_detail);
      setValue("stock", product.stock);
      setPOldImages(product?.image.map((image, index) => ({ id: `old-${index}`, url: image })));
    }
  }, [product, setValue]);
  
  // Update form values when images change
  useEffect(() => {
    setValue("productImages", productImages);
    trigger("productImages");
  }, [productImages, setValue, trigger]);

  // Add this effect to manage object URLs
  useEffect(() => {
    // Create object URLs for new images
    const urls = productImages.map(file => URL.createObjectURL(file));
    setImageUrls(urls);

    // Cleanup function to revoke object URLs
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [productImages]);

  // Show loading state while data is being fetched
  if (isLoadingProducts || isSubCategoryLoading || brandsLoading || !need) {
    return <Loading />;
  }

  // Show error if product is not found
  if (!product) {
    return <div className="p-4 text-red-500">Product not found</div>;
  }

  const onSubmit = async (data) => {
    try {
      setIsUpdating(true); // Show loading overlay
      
      // Create a FormData object for the API request
      const formData = new FormData();
      formData.append("id", productId);
      formData.append("removedImages", removedImages);
      formData.append("user", id);
      formData.append("product_name", data.productName);
      formData.append("brand", data.brand);
      formData.append("condition", data.condition);
      formData.append("description", data.description);
      formData.append("category", data.category);
      formData.append("price", data.price.replace(/,/g, ""));
      formData.append("stock", data.stock);
      formData.append("need", need);
      formData.append("cover_detail", data.coverDetail);
      formData.append("imageOrder", JSON.stringify(iOrder));

      // Create a temporary product for optimistic UI update
      const tempProduct = {
        _id: productId, 
        user: id,
        product_name: data.productName,
        brand: data.brand,
        condition: data.condition,
        description: data.description,
        category: data.category,
        price: data.price,
        stock: data.stock,
        need: need,
        cover_detail: data.coverDetail,
        // Handle both existing and new images based on iOrder
        image: iOrder.length > 0 
          ? iOrder.map(index => {
              // If index >= 10, it's an existing image from product.image
              // If index < 10, it's a new image from imageUrls
              return index >= 10 
                ? product.image[index - 10]  // Subtract 10 to get correct index in product.image
                : imageUrls[index]; // Use pre-created object URL
            })
          : product.image,
        createdAt: new Date().toISOString()
      };

      // Optimistically add to store before the API call
      dispatch(editInStore(tempProduct));
      
      // Add product images to form data
      // IMPORTANT: The field name must match what multer expects on the server
      // In your product.js route, it's expecting 'image', not 'productImages'
      productImages.forEach((file) => {
        formData.append("productImages", file);
      });

      console.log("Sending form data with fields:", Object.fromEntries(formData.entries()));

      // Make the API call
      const response = await updateProduct(formData).unwrap();
      
      console.log("Server response:", response);
      
      // Update the toast on success
      toast.success("Product created successfully!");
      
      // Update the store with the real product from the server
      if (response && response.product) {
        console.log("In update change store......");
        dispatch(updateStoreAfterEdit({ _id: productId, serverProduct: response.product }));
        
        // Navigate to the store page after a short delay
        setTimeout(() => { 
          navigate(ROUTES.MY_STORE);
        }, 100);
      } else {
        console.error("Invalid server response structure:", response);
        toast.error("Server response missing product data");
      }
    } catch (error) {
      console.error("Error creating product:", error);
      toast.error(error?.data?.message || "Failed to create product");
    } finally {
      setIsUpdating(false); // Hide loading overlay
    }
  };

  const inputColor = "bg-slate-200";

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-8 px-4">
      {isUpdating && <LoadingOverlay />}
      <div className="max-w-7xl mx-auto">
        <p className={isError ? "text-red-500 mb-4" : "hidden"}>{error?.data?.message}</p>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Product Type Display */}
          <h2 className="text-xl py-2 px-4 border-2 border-dashed border-slate-200 rounded-lg font-semibold text-slate-400 mb-4">UPDATE POST</h2>
          
          {/* Post Type Display */}
          <div className="bg-gradient-to-br from-[#a7efff] via-[#68d8f7] to-[#79dbff] p-6 rounded-lg shadow-xl">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4">Post Type</h2>
            <div className="flex items-center justify-center h-12 bg-black/70 text-neutral-300 rounded-md font-semibold">
              {need === "Sell" ? "This is a Sales Post" : "This is a Request Post"}
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-whit p-6 rounded-lg shadow-lg bg-slate-200">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Product Name */}
              <div className="space-y-1">
                <Controller name="productName" control={control} render={({ field }) => 
                  ( <InputBox usedFor="productName" boxLabel="Product Name" color={inputColor} onChange={(e) => field.onChange(e.target.value)} value={field.value} mustFill={true} /> )}
                />
                {errors.productName && <p className="text-red-500 text-sm">{errors.productName.message}</p>}
                <p className="text-[.9rem] text-gray-400 italic">Enter a clear and descriptive name</p>
              </div>
              {/* Brand */}
              <div className="space-y-1">
                <Controller name="brand" control={control} render={({ field }) => 
                  ( <DropdownInput usedFor="brand" boxLabel="Brand" color={inputColor} data={brands} onChange={(e) => field.onChange(e.target.value)} value={field.value} currentValue={currentBrand} mustFill={need==="Sell" ? true : false} /> )} />
                {errors.brand && <p className="text-red-500 text-sm">{errors.brand.message}</p>}
              </div>
              {/* Category */}
              <div className="space-y-1">
                <Controller name="category" control={control} render={({ field }) => 
                  ( <DropdownInput 
                      usedFor="category" 
                      boxLabel="Category" 
                      color={inputColor} 
                      data={subCategories} 
                      onChange={(e) => field.onChange(e.target.value)} 
                      value={field.value} 
                      currentValue={currentSubCategory}
                      mustFill={need==="Sell" ? true : false} 
                    /> )}
                />
                {errors.category && <p className="text-red-500 text-sm">{errors.category.message}</p>}
                <p className="text-[.9rem] text-gray-400 italic">Choose the most appropriate category</p>
              </div>
              {/* Condition */}
              <div className="space-y-1">
                <Controller name="condition" control={control} render={({ field }) => 
                  ( <DropdownInput usedFor="condition" boxLabel="Condition" color={inputColor} data={productCondition} onChange={(e) => field.onChange(e.target.value)} value={field.value} currentValue={product?.condition} mustFill={need==="Sell" ? true : false} /> )}
                />
                {errors.condition && <p className="text-red-500 text-sm">{errors.condition.message}</p>}
              </div>
            </div>
          </div>

          {/* Pricing and Stock */}
          <div className="bg-slate-200 p-6 rounded-lg shadow-lg">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Pricing and {need==="Request" ? "Amount" : "Stock"}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <Controller name="price" control={control} render={({ field }) => 
                  (<InputBox usedFor="price" boxLabel="Price" color={inputColor} onChange={(e) => {
                    const processedValue = handlePriceInput(e.target.value);
                    field.onChange(processedValue);
                  }} value={field.value ? formatNumber(field.value) : ''} mustFill={true} />)}
                />
                {errors.price && <p className="text-red-500 text-sm">{errors.price.message}</p>}
              </div>

              <div className="space-y-1">
                <Controller name="stock" control={control} render={({ field }) => 
                  (<InputBox usedFor="stock" boxLabel={need==="Request" ? "Amount" : "Stock"} color={inputColor} onChange={(e) => {
                    const processedValue = handleWholeNumberInput(e.target.value);
                    field.onChange(processedValue);
                  }} value={field.value ? formatNumber(field.value) : ''} mustFill={need==="Sell" ? true : false} />)}
                />
                {errors.stock && <p className="text-red-500 text-sm">{errors.stock.message}</p>}
                <p className="text-[.9rem] text-gray-400 italic">{need==="Request" ? "Enter the amount you want to request" : "Number of items available for sale"}</p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-slate-200 p-6 rounded-lg shadow-lg">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Description</h2>
            <div className="space-y-6">
              <div className="space-y-1">
                <Controller name="description" control={control}
                  render={({ field }) => (
                    <RichTextEditor value={field.value} onChange={field.onChange} className="w-full" />
                  )}
                />
                {errors.description && <p className="text-red-500 text-sm">{errors.description.message}</p>}
                <p className="text-[.9rem] text-gray-400 italic">Provide detailed information about your product. Include features, specifications, and any important details.</p>
              </div>

              <div className="space-y-1">
                <Controller name="coverDetail" control={control} render={({ field }) => (
                  <InputBox usedFor="coverDetail" boxLabel="Cover Detail" color={inputColor} onChange={(e) => field.onChange(e.target.value)} value={field.value} mustFill={true} /> )}
                />
                {errors.coverDetail && <p className="text-red-500 text-sm">{errors.coverDetail.message}</p>}
                <p className="text-[.9rem] text-gray-400 italic">Write a sentence long cover detail for thumbnail</p>
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="bg-slate-200 p-6 rounded-lg shadow-lg">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Product Images</h2>
            <div className="space-y-1">
              <MultipleImageInput pOldImages={pOldImages} setPOldImages={setPOldImages} setProductImages={setProductImages} iOrder={iOrder} setIOrder={setIOrder} setRemovedImages={setRemovedImages} />
              {isSubmitted && errors.productImages && (
                <p className="text-red-500 text-sm">{errors.productImages.message}</p>
              )}
              <p className="text-[.9rem] text-gray-400 italic">Upload 1-5 clear images of your product. First image will be used as the cover image.</p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button 
              type="submit" 
              disabled={isUpdating} 
              className={`w-full md:w-[140px] h-12 bg-neutral-900 text-white text-[1.1rem] rounded-md transition-opacity ${
                isUpdating ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Update Product
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateProduct;